import type {
  ValidateAddressInput,
  ValidateAddressRequest,
  ValidateAddressUserType,
} from '../../../core/models/address-validate';
import type { AddressFormValue, ShipmentAddressesFormValue } from '../models/address-form.model';
import { resolveValidateAddressLine2 } from './resolve-validate-address-line2';

type ValidateAddressSectionFields = Pick<
  AddressFormValue,
  | 'streetAddress'
  | 'suiteAptUnit'
  | 'entryBuzzerCode'
  | 'city'
  | 'province'
  | 'postalCode'
  | 'residential'
> & {
  addressLine2?: string;
  addressLine3?: string;
  attentionTo?: string;
  companyOrFullName?: string;
  email?: string;
  phoneNumber?: string;
  phoneExtension?: string;
};

/**
 * Maps a create-shipment address section to a POST /addresses/validate address entry.
 * Return addresses reuse streetAddress as addressee; sender/recipient use companyOrFullName.
 * Customer-entered contacts are forwarded so AVS can preserve them in its signed attestation;
 * the create SUCCESS response can then return them even when Canada Post removes `addressee`.
 * Forwards residential from retrieve type (default false for manual entry).
 *
 * `suiteAptUnit` is sent BOTH ways intentionally, not redundantly: it stays folded into
 * `addressLine2` via `resolveValidateAddressLine2()` because that is the only field the backend
 * forwards to the Canada Post ValidateAddress2 SOAP call (see
 * `dsc-online-shipping-be/api/address-validation-service/canada-post/address-validation/map-validate-address-input.ts`,
 * which reads only `addressLine1`/`addressLine2`); it is ALSO sent as its own `suiteAptUnit`
 * field so the backend can persist it verbatim in the signed attestation (see
 * `map-address-validation-attestation.ts`), the same way `companyName`/`email`/`phoneNumber`
 * already are. `entryBuzzerCode` has no Canada Post equivalent, so it is only ever sent as its
 * own field for attestation persistence.
 *
 * Free-text Address Line 2 / Line 3 and contact name (`attentionTo`) are sent as dedicated
 * attestation fields (`optionalAddressLine2`, `addressLine3`, `attentionTo`) so Info Hub and
 * CSOP keep the shipper's typed values instead of AVS city/province/postal composites.
 */
function mapAddressSectionToValidateInput(
  userType: ValidateAddressUserType,
  section: ValidateAddressSectionFields,
): ValidateAddressInput {
  const addressLine2 = resolveValidateAddressLine2(section);
  const streetAddress = section.streetAddress.trim();
  const addressee =
    userType === 'returnTo' ? streetAddress : (section.companyOrFullName ?? '').trim();
  const companyName = section.companyOrFullName?.trim() ?? '';
  const email = section.email?.trim() ?? '';
  const phoneNumber = section.phoneNumber?.trim() ?? '';
  const phoneExtension = section.phoneExtension?.trim() ?? '';
  const suiteAptUnit = section.suiteAptUnit?.trim() ?? '';
  const entryBuzzerCode = section.entryBuzzerCode?.trim() ?? '';
  const optionalAddressLine2 = section.addressLine2?.trim() ?? '';
  const addressLine3 = section.addressLine3?.trim() ?? '';
  const attentionTo = section.attentionTo?.trim() ?? '';

  return {
    userType,
    addressee,
    ...(companyName ? { companyName } : {}),
    addressLine1: streetAddress,
    ...(addressLine2.length > 0 ? { addressLine2 } : {}),
    municipalityName: section.city.trim(),
    provinceCode: (section.province ?? '').trim().toUpperCase(),
    postalCode: section.postalCode.replace(/\s+/g, ''),
    ...(email ? { email } : {}),
    ...(phoneNumber ? { phoneNumber } : {}),
    ...(phoneExtension ? { phoneExtension } : {}),
    ...(suiteAptUnit ? { suiteAptUnit } : {}),
    ...(entryBuzzerCode ? { entryBuzzerCode } : {}),
    ...(optionalAddressLine2 ? { optionalAddressLine2 } : {}),
    ...(addressLine3 ? { addressLine3 } : {}),
    ...(attentionTo ? { attentionTo } : {}),
    residential: section.residential === true,
  };
}

/**
 * Maps ship-from-ship-to address form values to POST /addresses/validate request body.
 */
export function mapShipmentAddressesToValidateRequest(
  formValue: ShipmentAddressesFormValue,
): ValidateAddressRequest {
  const addresses: ValidateAddressInput[] = [
    mapAddressSectionToValidateInput('sender', formValue.shipFrom),
    mapAddressSectionToValidateInput('recipient', formValue.shipTo),
  ];

  if (formValue.useDifferentReturnAddress) {
    addresses.push(mapAddressSectionToValidateInput('returnTo', formValue.returnAddress));
  }

  return { addresses };
}
